import { DecimalField, FormGroup, useField, useForm, useIsDisabled } from '@ff-it/form';
import { Box, Select, Table, Tbody, Td, Th, Thead, Tr } from '@ff-it/ui';
import { FormInlineError } from 'components';
import { useFieldValue } from 'hooks';
import type { Outlet } from 'modules/supplier/outletOrServiceCategory/types';
import type { ReactElement, ReactNode } from 'react';
import { getIn } from 'final-form';

import { BandRateTable } from '../common/BandRateTable';
import type { RadioPackagePricing, RadioTimeBands } from '../types';
import {
  catalogPricingKindLabel,
  optionField,
  type PackagePricingKind,
  type PricingDrafts,
  switchPackagePricing,
} from './formValues';

const pricingKindOptions = (['RATE_CARD', 'DISCOUNT'] as const).map((value) => ({
  value,
  label: catalogPricingKindLabel(value),
}));

export function PricingKindField({ name = '' }: { name?: string }): ReactElement {
  const form = useForm();
  const disabled = useIsDisabled();
  const fieldName = optionField(name, 'pricing.kind');
  const {
    input: { name: selectName, value, onBlur, onFocus },
  } = useField<PackagePricingKind>(fieldName, { subscription: { value: true } });

  return (
    <FormGroup label="Pricing kind" id={fieldName}>
      <Select
        name={selectName}
        inputId={fieldName}
        disabled={disabled}
        value={value as any}
        onBlur={onBlur}
        onFocus={onFocus}
        simple
        required
        isClearable={false}
        options={pricingKindOptions}
        onChange={(nextKind) => {
          const values = form.getState().values;
          const pricing = getIn(values, optionField(name, 'pricing')) as RadioPackagePricing;
          const drafts = getIn(values, optionField(name, 'pricing_drafts')) as PricingDrafts;
          const next = switchPackagePricing(pricing, drafts, nextKind as unknown as PackagePricingKind);
          form.batch(() => {
            form.change(optionField(name, 'pricing_drafts'), next.pricing_drafts);
            form.change(optionField(name, 'pricing'), next.pricing);
          });
        }}
      />
      <FormInlineError name={fieldName} className="mt-1" />
    </FormGroup>
  );
}

function OutletValueTable({
  outlets,
  render,
  label,
}: {
  outlets: Outlet[];
  render: (outlet: Outlet) => ReactNode;
  label: ReactNode;
}): ReactElement {
  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table size="xs" textSize="sm" border="subtle" fixed>
        <Thead variant="default">
          <Tr>
            <Th>{label}</Th>
            {outlets.map((outlet) => (
              <Th key={outlet.id} textAlign="right">
                {outlet.name}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td />
            {outlets.map((outlet) => (
              <Td key={outlet.id} textAlign="right">
                {render(outlet)}
              </Td>
            ))}
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
}

function RateCardPricingEditor({
  name,
  bands,
  outlets,
}: {
  name: string;
  bands: RadioTimeBands;
  outlets: Outlet[];
}): ReactElement {
  const ratesName = optionField(name, 'pricing.card.rates');
  return (
    <Box>
      <FormInlineError name={optionField(name, 'pricing.card')} className="mb-1" />
      <FormInlineError name={ratesName} className="mb-1" />
      <Box style={{ overflowX: 'auto' }}>
        <BandRateTable
          bands={bands}
          columns={outlets.map((outlet) => ({
            key: outlet.id,
            header: outlet.name,
            render: (_band, _dayType, bandKey) => (
              <DecimalField
                name={`${ratesName}.${bandKey}._${outlet.id}`}
                className="mb-0 text-right"
                size="sm"
                precision={2}
              />
            ),
          }))}
        />
      </Box>
    </Box>
  );
}

function DiscountPricingEditor({ name, outlets }: { name: string; outlets: Outlet[] }): ReactElement {
  const percentagesName = optionField(name, 'pricing.percentages');
  return (
    <Box>
      <FormInlineError name={percentagesName} className="mb-1" />
      <OutletValueTable
        label="Discount %"
        outlets={outlets}
        render={(outlet) => (
          <DecimalField name={`${percentagesName}._${outlet.id}`} className="mb-0 text-right" size="sm" precision={6} />
        )}
      />
    </Box>
  );
}

export function PricingEditor({
  name = '',
  bands,
  outlets,
}: {
  name?: string;
  bands: RadioTimeBands;
  outlets: Outlet[];
}): ReactElement {
  const kind = useFieldValue<PackagePricingKind>(optionField(name, 'pricing.kind'), { allowNull: false });
  return (
    <Box>
      <FormInlineError name={optionField(name, 'pricing')} className="mb-1" />
      {kind === 'RATE_CARD' ? (
        <RateCardPricingEditor name={name} bands={bands} outlets={outlets} />
      ) : (
        <DiscountPricingEditor name={name} outlets={outlets} />
      )}
    </Box>
  );
}
